// Durable prayer-update enrichment (offline-first Chunk 2). The success-path
// logic that used to live inline in usePrayerUpdate.commitUpdate, lifted into
// one reusable async function so an update committed offline eventually gets
// its re-parse / gardenSummary / status / per-entry recommendedStage once back
// online, durably across reload, with no wrong moments, no lost concurrent
// requests, and no third Grok call (the rule-#51 budget stays 2). See plan
// §3.2. Driven by usePrayerUpdate (online, fire-and-forget) and
// usePrayerEnrichmentRetry (the reconnect/boot worker).
import * as db from '@/lib/db';
import { useAdiaStore } from '@/lib/store';
import { parsePrayer, generateGardenSummary } from '@/lib/grok';
import { buildPrivacySafeEnrichmentContext } from '@/lib/privacy-safe-enrichment';
import { hasSyncSettled, hasCloudAccount } from '@/lib/sync-events';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { getContentOwnerBinding } from '@/lib/content-owner';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import type { ParsedPrayer, Prayer } from '@/types/prayer';
import {
  enrichmentTarget,
  pendingTokensEqual,
  progressEntryIds,
} from '@/lib/progress-entry-identity';
import { prayerContentMatches, prayerContentSnapshot, prayerOutboundExpectation } from '@/lib/outbound-prayer-authorization';

// Per-prayer single-flight so concurrent triggers (commitUpdate + the retry
// worker, or two retry ticks) never run redundant Grok work for one prayer.
const inFlight = new Set<string>();

export async function refreshPrayerEnrichment(id: string): Promise<void> {
  // Core-content cutover: enrichment is a prayer MUTATION (it rewrites
  // parsed / gardenSummary / status and clears the durable token) and it
  // sends prayer text to Grok. Both stay off while read-only is active. The
  // pendingEnrichment tokens stay on their rows, so a later writes-enabled
  // deployment picks the work back up untouched.
  if (isCoreReadOnly()) return;
  const expectedOwnerId = getAuthUserId();
  const expectedAuthEpoch = getAuthEpoch();
  if (
    !expectedOwnerId ||
    (await getContentOwnerBinding()) !== expectedOwnerId
  ) return;
  const ownerIsCurrent = (): boolean => getAuthUserId() === expectedOwnerId && getAuthEpoch() === expectedAuthEpoch;
  if (inFlight.has(id)) return;
  inFlight.add(id);
  try {
    // 1. Read the AUTHORITATIVE IDB row (not the possibly-lagging store) so
    //    the Grok inputs and the guard baseline are consistent.
    const input = await db.getPrayer(id);
    if (!input) return;
    if (input.pendingTranscriptionId) return; // half-formed; leave marker
    if (!input.pendingEnrichment) return;

    // 1a. Per-DEVICE enrich rule (§3.4). A has-account device with no settled
    //     signed-in pull defers — a bump could clobber a (possibly lost-mark)
    //     cloud counterpart. The worker re-fires on adia:sync-settled. A
    //     never-account device passes: no cloud exists to clobber. LEAVE the
    //     token; do NOT re-invoke here (that would busy-loop) — the
    //     SYNC_SETTLED_EVENT / ENRICHMENT_RECHECK retry tick re-runs the worker.
    if (!hasSyncSettled() && hasCloudAccount()) return;

    // 2. Resolve the token's entry. If the target update was deleted, clear
    //    the token locally (no push) and release.
    const targetValue = input.pendingEnrichment;
    const target = enrichmentTarget(input);
    if (!target || target.token.sourceUpdatedAt !== input.updatedAt) {
      await useAdiaStore.getState().clearPendingEnrichment(id, targetValue);
      return;
    }
    const targetEntry = input.progressHistory[target.index];

    // Content, not wallclock time, identifies the provider inputs and fields
    // this completion may overwrite. Local tokens are checked separately.
    const inputSnapshot = prayerContentSnapshot(input);

    // 4. Exact thread builder — mirrors the online reparse: rawText + the
    //    single target update's note. parsed reflects the raw prayer plus the
    //    newest update's note, the same shape the online path uses.
    const fullText = input.rawText + '\n\nUpdate: ' + targetEntry.note;
    const { relatedContext, existingPeople, contextExpectations } = buildPrivacySafeEnrichmentContext(
      fullText,
      useAdiaStore.getState().prayers,
      id,
    );

    // 5. The two existing Grok calls (the whole rule-#51 budget). On EITHER
    //    throwing (offline / Grok error): leave the token, do NOT re-invoke
    //    (the reconnect worker retries), release.
    let parsed: ParsedPrayer;
    let summaryResult: Awaited<ReturnType<typeof generateGardenSummary>>;
    try {
      const results = await Promise.allSettled([
        parsePrayer(fullText, relatedContext || undefined, existingPeople, {
          target: prayerOutboundExpectation(input),
          context: contextExpectations,
        }),
        generateGardenSummary(input),
      ]);
      // Keep the single-flight slot until BOTH existing calls settle, even
      // when one rejects early. A wake cannot overlap its billed sibling.
      const [parseResult, summary] = results;
      if (parseResult.status === 'rejected' || summary.status === 'rejected') {
        throw new Error('prayer-enrichment-provider-unavailable');
      }
      parsed = parseResult.value;
      summaryResult = summary.value;
    } catch {
      throw new Error('prayer-enrichment-provider-unavailable');
    }
    if (!ownerIsCurrent()) return;

    // 6. The lifecycle CAS evaluates this against its authoritative snapshot
    // and revalidates that snapshot when committing. Only the token-identified
    // entry is classified; all recorded dates are carried unchanged.
    const computeNext = (row: Prayer): Prayer => {
      const edits = row.userEditedFields ?? {};
      // Preserve a manual `who` edit (and the mentionedPeople keyed off it),
      // the usePrayerUpdate.reparseInBackground pattern (rule #52).
      const mergedParsed: ParsedPrayer =
        edits.who && row.parsed
          ? {
              ...parsed,
              who: row.parsed.who,
              mentionedPeople: row.parsed.mentionedPeople,
            }
          : parsed;

      const statusEdited = !!edits.status;
      let status = row.status;
      if (
        summaryResult.recommendedStage === 'God is moving' &&
        row.status !== 'Answered' &&
        !statusEdited
      ) {
        status = 'Moving';
      } else if (
        summaryResult.recommendedStage === 'Still praying' &&
        row.status !== 'Answered' &&
        !statusEdited
      ) {
        status = 'Open';
      }

      let progressHistory = row.progressHistory;
      if (summaryResult.recommendedStage !== null) {
        const entryIds = progressEntryIds(row.id, row.progressHistory);
        progressHistory = row.progressHistory.map((e, index) =>
          entryIds[index] === target.entryId && e.note === targetEntry.note
            ? { ...e, recommendedStage: summaryResult.recommendedStage }
            : e,
        );
      }

      return {
        ...row,
        parsed: mergedParsed,
        gardenSummary: summaryResult.summary,
        status,
        progressHistory,
        pendingEnrichment: null,
      };
    };

    // 7. The lifecycle CAS protects the snapshot through commit, including
    // concurrent pulls. A changed canonical document, local token, owner or
    // settle state refuses the completion instead of overwriting newer work.
    await useAdiaStore.getState().applyEnrichment(
      id,
      (row) => {
        const currentTarget = enrichmentTarget(row);
        return (
          prayerContentMatches(inputSnapshot, row) &&
          pendingTokensEqual(row.pendingEnrichment, targetValue) &&
          currentTarget?.entryId === target.entryId &&
          currentTarget.token.sourceUpdatedAt === row.updatedAt &&
          ownerIsCurrent() &&
          (hasSyncSettled() || !hasCloudAccount())
        );
      },
      computeNext,
    );
  } finally {
    inFlight.delete(id);
  }

  // A refused completion leaves its durable token for existing external
  // reconnect/visibility/settle wakes. It never schedules another paid call.
}
